/**
 * ホスティングの画面の「プロジェクト名」のカード（2026-09-28 creator/project-rename・AGT-10）。今の名前を入れた入力欄 + 保存。
 * 判断（空・80 文字超・変わっていない・エラーの文言）は projectRename.ts（テストあり）。API の正は Core の platform/projectRename.ts。
 *
 * WHY ホスティングの画面（URL スラッグのカードの直前）に置くか: ゲームに「設定」の画面は無い（SlugSettings.tsx の WHY と同じ）。
 *   名前とスラッグはどちらも「プロジェクト全体の、外に見える名前」で、並べると「名前 = メールに出る / スラッグ = URL に出る」の違いが
 *   1 か所で分かる。どちらの環境を見ていても同じ値なので、環境に関わらず出す。`…/hosting#project-name` で直接このカードへ来られる。
 * WHY 確認のダイアログ・再認証を挟まないか: サーバーも挟まない（取り返しがつく・料金も無い・何度でも戻せる。持ち主への通知メールは残る）。
 *   利用者を騙す名前はサーバーの name_reserved で止まる。
 * 保存の後: onRenamed で枠（ProjectShell）のプロジェクトを読み直す = サイドバーの名前が新しくなる。ホームの一覧は開くたびに
 *   GET /v1/projects を読み直す（hooks.ts の useResource・共有のキャッシュは無い）ので、ここで消すものは無い。
 */
import { useEffect, useState, type FormEvent } from 'react';
import { useLocation } from 'react-router-dom';
import { api, ApiError } from '../api';
import { errorText, useT } from '../i18n';
import {
  canSaveProjectName,
  PROJECT_NAME_MAX,
  projectNameLength,
  projectNameState,
  projectPath,
  renameBody,
  renameErrorKey,
  renameNotice,
  type NoticeText,
  type ProjectRenameResult
} from '../projectRename';

export function ProjectNameCard({ projectId, name, onRenamed }: { projectId: string; name: string; onRenamed: (res: ProjectRenameResult) => void }) {
  const { t } = useT();
  const location = useLocation();
  const [draft, setDraft] = useState(name);
  // 保存に成功した名前（枠の読み直しが終わるまでの間の「今の名前」）。読み直しで props の name が追いついたら null に戻す
  const [saved, setSaved] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // 知らせ・エラーはキーと値だけ持ち、表示時に翻訳する（言語を切り替えても今の言語で出る。SlugSettings と同じ）
  const [notice, setNotice] = useState<NoticeText | null>(null);
  const [error, setError] = useState<ApiError | null>(null);

  // 枠の名前が変わった（保存の後の読み直し・別のプロジェクトへ移った）ら、入力欄を今の名前に合わせる
  useEffect(() => {
    setDraft(name);
    setSaved(null);
  }, [name]);
  // 別のプロジェクトへ移ったら前のプロジェクトの知らせを残さない
  useEffect(() => {
    setNotice(null);
    setError(null);
  }, [projectId]);

  // 案内の URL（…/hosting#project-name）から来たら、このカードまで動かす（react-router は # へ動かさない）
  useEffect(() => {
    if (location.hash === '#project-name') document.getElementById('project-name')?.scrollIntoView({ block: 'start' });
  }, [location.hash]);

  const current = saved ?? name;
  const state = projectNameState(draft, current);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSaveProjectName(draft, current, busy)) return;
    setBusy(true);
    setNotice(null);
    setError(null);
    try {
      const res = await api<ProjectRenameResult>(projectPath(projectId), { method: 'PATCH', body: renameBody(draft) });
      // 入力欄はサーバーが掃除した後の名前にする（保存された形そのものを見せる。changed:false でも空白の違い等を揃える）
      setSaved(res.project.name);
      setDraft(res.project.name);
      setNotice(renameNotice(res));
      if (res.changed) onRenamed(res);
    } catch (err) {
      setError(err instanceof ApiError ? err : new ApiError(0, 'client_error', String(err)));
    } finally {
      setBusy(false);
    }
  };

  const errorKey = renameErrorKey(error);
  const len = projectNameLength(draft);

  return (
    <section className="card" id="project-name">
      <h2 className="card-title">{t('projectName.title')}</h2>
      <p className="small muted">{t('projectName.lead')}</p>
      {notice && (
        <p className="alert alert-ok" role="status">
          {t(notice.key, notice.vars)}
        </p>
      )}
      {error && (
        <p className="alert alert-error" role="alert">
          {errorKey ? t(errorKey, { max: PROJECT_NAME_MAX }) : errorText(error)}
        </p>
      )}
      <form className="form-inline" onSubmit={submit}>
        <label className="field field-grow">
          <span>
            {t('projectName.label')}{' '}
            <small className={state === 'tooLong' ? 'field-error' : 'muted'}>
              {t('projectName.count', { count: len, max: PROJECT_NAME_MAX })}
            </small>
          </span>
          {/* maxLength は付けない: 貼り付けた長い名前が黙って切れると、本人の知らない名前で保存される。超えたら下の文と保存の無効で知らせる */}
          <input
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              setNotice(null);
            }}
            aria-invalid={state === 'tooLong' || undefined}
            aria-describedby={state === 'tooLong' ? 'project-name-too-long' : undefined}
            autoComplete="off"
          />
        </label>
        <button type="submit" className="btn btn-primary" disabled={!canSaveProjectName(draft, current, busy)}>
          {busy ? t('projectName.saving') : t('projectName.save')}
        </button>
      </form>
      {state === 'tooLong' && (
        <p className="small field-error" id="project-name-too-long">
          {t('projectName.tooLong', { max: PROJECT_NAME_MAX })}
        </p>
      )}
    </section>
  );
}
